import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar, CheckCircle2, IndianRupee, Users } from 'lucide-react';
import { motion } from 'framer-motion';

function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div 
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1750716413756-b66624b64ce4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxjcmlja2V0JTIwc3RhZGl1bSUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc3MDk3NzU5MHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900/60"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <motion.img 
            src="/images/weakenders-logo.png"
            alt="Weakenders Logo"
            className="w-48 h-48 mx-auto mb-6 drop-shadow-2xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="team-logo"
          />
          <motion.h1 
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            THE WEAKENDERS
          </motion.h1>
          <motion.p 
            className="text-xl sm:text-2xl text-slate-200 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Your Cricket Team Management Platform
          </motion.p>
          <motion.div 
            className="flex gap-4 justify-center flex-wrap"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              onClick={() => navigate('/register')}
              className="bg-[#E63946] hover:bg-[#DC2626] text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-lg shadow-lg"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-white hover:bg-slate-100 text-[#0F172A] font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-lg shadow-lg"
              data-testid="login-btn"
            >
              Login
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-center text-[#0F172A] mb-12">Why Weakenders?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard
            icon={<Calendar className="w-10 h-10 text-[#E63946]" />}
            title="Easy RSVP"
            description="Quick IN/OUT for Saturday & Sunday matches"
          />
          <FeatureCard
            icon={<Users className="w-10 h-10 text-[#E63946]" />}
            title="Auto Waitlist"
            description="First-come-first-served with auto-promotion"
          />
          <FeatureCard
            icon={<IndianRupee className="w-10 h-10 text-[#E63946]" />}
            title="Smart Payments"
            description="Automatic cost splitting & UPI collection"
          />
          <FeatureCard
            icon={<CheckCircle2 className="w-10 h-10 text-[#E63946]" />}
            title="Team Selection"
            description="Admin-controlled Saturday, auto Sunday"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0F172A] text-white py-8 text-center">
        <p className="text-slate-300">© 2025 The Weakenders.</p>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <motion.div 
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center hover:shadow-md transition-shadow"
      whileHover={{ y: -5 }}
      data-testid={`feature-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex justify-center mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-slate-600">{description}</p>
    </motion.div>
  );
}

export default Landing;